<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content d-flex justify-content-between align-items-center">
                          <h4 class="nk-block-title mb-0">{{ $t('dm_kalk_title') }}</h4>
                          <span v-if="preisliste" class="text-muted small">Version: {{ preisliste.version }}</span>
                        </div>
                      </div>

                      <div v-if="!preisliste && !loading" class="alert alert-warning">{{ $t('dm_kalk_no_preisliste') }}</div>

                      <div v-if="preisliste">
                        <!-- AI Assistent -->
                        <div class="dm-ai-bar mb-4">
                          <div class="dm-ai-inner">
                            <div class="dm-ai-icon">
                              <em class="icon ni ni-spark"></em>
                            </div>
                            <textarea class="dm-ai-input" v-model="aiText" :placeholder="$t('dm_kalk_ai_placeholder')" rows="1" @keydown.enter.exact.prevent="runAiParse" @input="autoResizeAi"></textarea>
                            <button class="btn btn-sm dm-ai-btn" :disabled="!aiText.trim() || aiLoading" @click="runAiParse">
                              <span v-if="aiLoading" class="spinner-border spinner-border-sm"></span>
                              <em v-else class="icon ni ni-send"></em>
                            </button>
                          </div>
                          <div v-if="aiError" class="dm-ai-error">{{ aiError }}</div>
                          <div v-if="aiUsage" class="dm-ai-usage">{{ aiUsage.total_tokens.toLocaleString('de-DE') }} tokens</div>
                        </div>

                        <!-- Kunde / Park Selection (collapsible) -->
                        <div class="dm-collapse-header mb-4" @click="showKunde = !showKunde">
                          <em class="icon ni" :class="showKunde ? 'ni-chevron-down' : 'ni-chevron-right'"></em>
                          <span>{{ $t('dm_kalk_section_kunde') }}</span>
                          <span v-if="!showKunde && (selectedKunde || projektName)" class="dm-collapse-hint">{{ selectedKunde ? selectedKunde.firma : '' }}{{ selectedKunde && projektName ? ' — ' : '' }}{{ projektName }}</span>
                        </div>
                        <div class="card card-bordered mb-4" v-show="showKunde">
                          <div class="card-inner">
                            <div class="row g-4">
                              <div class="col-md-4">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_kunde') }}</label>
                                  <select class="form-control" v-model="selectedKundeId" @change="onKundeChange">
                                    <option value="">-- {{ $t('dm_kalk_select') }} --</option>
                                    <option v-for="a in adressen" :key="a._id" :value="a._id">{{ a.firma }}{{ a.ansprechpartner ? ' — ' + a.ansprechpartner : '' }}</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-md-4">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_park') }}</label>
                                  <select class="form-control" v-model="selectedParkId">
                                    <option value="">-- {{ $t('dm_kalk_select') }} --</option>
                                    <option v-for="p in parks" :key="p._id" :value="p._id">{{ p.name }}{{ p.standort_name ? ' (' + p.standort_name + ')' : '' }}</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-md-4">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_projekt') }}</label>
                                  <input type="text" class="form-control" v-model="projektName" :placeholder="$t('dm_kalk_placeholder_projekt')" />
                                </div>
                              </div>
                            </div>
                            <!-- Selected customer details -->
                            <div v-if="selectedKunde" class="dm-wtg-badges mt-3 pt-3" style="border-top: 1px solid rgba(128, 128, 128, 0.15);">
                              <span class="badge badge-dim badge-outline-gray" v-if="selectedKunde.strasse">{{ selectedKunde.strasse }}, {{ selectedKunde.plz }} {{ selectedKunde.ort }}</span>
                              <span class="badge badge-dim badge-outline-gray" v-if="selectedKunde.email">{{ selectedKunde.email }}</span>
                              <span class="badge badge-dim badge-outline-gray" v-if="selectedKunde.telefon">{{ selectedKunde.telefon }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- WTG Selection (shared between both modes) -->
                        <div class="card card-bordered mb-4">
                          <div class="card-inner">
                            <h6 class="overline-title overline-title-alt mb-4">{{ $t('dm_kalk_section_wtg') }}</h6>
                            <div class="row g-4">
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_pl_col_hersteller') }}</label>
                                  <select class="form-control" v-model="selectedHersteller" @change="onHerstellerChange">
                                    <option value="">-- {{ $t('dm_kalk_select') }} --</option>
                                    <option v-for="h in herstellerList" :key="h" :value="h">{{ h }}</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_pl_col_typ') }}</label>
                                  <select class="form-control" v-model="selectedTyp" @change="onTypChange" :disabled="!selectedHersteller">
                                    <option value="">-- {{ $t('dm_kalk_select') }} --</option>
                                    <option v-for="t in filteredTypen" :key="t.typ" :value="t.typ">{{ t.typ }}</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_pl_col_nabenhoehen') }}</label>
                                  <select class="form-control" v-model.number="selectedNH" :disabled="!selectedTyp">
                                    <option value="0">-- {{ $t('dm_kalk_select') }} --</option>
                                    <option v-for="nh in wtgNabenhoehen" :key="nh" :value="nh">{{ nh }} m</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_anzahl_wea') }}</label>
                                  <input type="number" min="1" class="form-control" v-model.number="anzahlWEA" />
                                </div>
                              </div>
                            </div>
                            <!-- Auto-populated WTG data -->
                            <div v-if="wtg" class="dm-wtg-badges mt-4 pt-3" style="border-top: 1px solid rgba(128, 128, 128, 0.15);">
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_durchmesser') }}: {{ wtg.durchmesser }} m</span>
                              <span class="badge badge-dim badge-outline-gray">kW: {{ wtg.kw }}</span>
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_turmtyp') }}: {{ wtg.turmtyp || '—' }}</span>
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_trafo') }}: {{ wtg.trafo_position || '—' }}</span>
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_getriebe') }}: {{ wtg.getriebe }}</span>
                              <span class="badge badge-dim badge-outline-gray">{{ $t('dm_pl_col_pruefzeit') }}: {{ wtg.pruefzeit }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Mode Tabs -->
                        <ul class="nav nav-tabs">
                          <li class="nav-item">
                            <a class="nav-link" :class="{ active: mode === 'standard' }" href="#" @click.prevent="mode = 'standard'">{{ $t('dm_kalk_tab_standard') }}</a>
                          </li>
                          <li class="nav-item">
                            <a class="nav-link" :class="{ active: mode === 'individuell' }" href="#" @click.prevent="mode = 'individuell'">{{ $t('dm_kalk_tab_individuell') }}</a>
                          </li>
                        </ul>

                        <div class="card card-bordered" style="border-top: none; border-top-left-radius: 0; border-top-right-radius: 0;">
                          <div class="card-inner">

                            <!-- STANDARD MODE -->
                            <div v-show="mode === 'standard'">
                              <!-- Hauptprüfung -->
                              <h6 class="overline-title overline-title-alt mb-3">{{ $t('dm_kalk_section_hauptpruefung') }}</h6>
                              <div class="row g-4 mb-4 pb-4" style="border-bottom: 1px solid rgba(128, 128, 128, 0.15);">
                                <div class="col-md-4">
                                  <div class="form-group">
                                    <label class="form-label">{{ $t('dm_kalk_label_pruefungstyp') }}</label>
                                    <select class="form-control" v-model="selectedPruefTyp">
                                      <option value="">-- {{ $t('dm_kalk_select') }} --</option>
                                      <option value="ibn">IBN (Inbetriebnahme)</option>
                                      <option value="eow">EoW (End of Warranty)</option>
                                      <option value="wkp_basic">WKP-basic</option>
                                      <option value="wkp_plus">WKP-Plus</option>
                                      <option value="zop">ZOP</option>
                                    </select>
                                  </div>
                                </div>
                                <div class="col-md-4">
                                  <div class="form-group">
                                    <label class="form-label">&nbsp;</label>
                                    <div class="form-control-wrap dm-checkbox-align">
                                      <div class="custom-control custom-checkbox">
                                        <input type="checkbox" class="custom-control-input" id="cbExclTrafo" v-model="exclTrafo" />
                                        <label class="custom-control-label" for="cbExclTrafo">{{ $t('dm_kalk_label_trafo_excl_hint') }}</label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div class="col-md-4">
                                  <div class="form-group">
                                    <label class="form-label">&nbsp;</label>
                                    <div class="form-control-wrap dm-checkbox-align">
                                      <div class="custom-control custom-checkbox">
                                        <input type="checkbox" class="custom-control-input" id="cbBegleit" v-model="begleitpersonProvided" />
                                        <label class="custom-control-label" for="cbBegleit">{{ $t('dm_kalk_label_begleitperson_hint') }}</label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <!-- Zusatzprodukte -->
                              <h6 class="overline-title overline-title-alt mb-3">{{ $t('dm_kalk_section_zusatz') }}</h6>
                              <div class="row g-3 mb-4 pb-4" style="border-bottom: 1px solid rgba(128, 128, 128, 0.15);">
                                <div class="col-md-6" v-for="zp in zusatzProdukte" :key="zp.key">
                                  <div class="dm-zusatz-row">
                                    <div class="custom-control custom-checkbox">
                                      <input type="checkbox" class="custom-control-input" :id="'zp_' + zp.key" v-model="zp.active" />
                                      <label class="custom-control-label" :for="'zp_' + zp.key">{{ zp.label }}</label>
                                    </div>
                                    <select v-if="zp.active && zusatzOptionen[zp.key] && zusatzOptionen[zp.key].length > 1" class="form-control form-control-sm ml-3" style="width: 220px;" v-model="zp.selected">
                                      <option v-for="opt in zusatzOptionen[zp.key]" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                                    </select>
                                  </div>
                                </div>
                              </div>

                              <!-- Included positions -->
                              <div v-if="wtg && (selectedPruefTyp || hasActiveZusatz)">
                                <h6 class="overline-title overline-title-alt mb-2">{{ $t('dm_kalk_section_positions') }}</h6>
                                <div v-for="(group, gIdx) in standardGroups" :key="gIdx" class="mb-3">
                                  <div class="dm-group-header-sm">
                                    <strong>{{ group.produkt }}</strong>
                                    <span class="float-right text-primary fw-bold">{{ formatPrice(group.subtotal) }} EUR</span>
                                  </div>
                                  <table class="dm-table dm-table-compact">
                                    <tbody>
                                      <tr v-for="(pos, pIdx) in group.items" :key="pIdx" :class="{ 'dm-row-excluded': pos.excluded }">
                                        <td style="width: 5%" v-if="pos.inclusion === 'ggf'">
                                          <div class="custom-control custom-checkbox">
                                            <input type="checkbox" class="custom-control-input" :id="'std_' + gIdx + '_' + pIdx" :checked="!pos.excluded" @change="toggleExcluded(pos._idx)" />
                                            <label class="custom-control-label" :for="'std_' + gIdx + '_' + pIdx"></label>
                                          </div>
                                        </td>
                                        <td style="width: 5%" v-else></td>
                                        <td style="width: 30%">{{ pos.modul }}</td>
                                        <td style="width: 30%" class="text-muted small">{{ pos.detail }}</td>
                                        <td style="width: 10%" class="text-center">
                                          <span class="badge" :class="pos.inclusion === 'x' ? 'badge-success' : 'badge-warning'">{{ pos.inclusion === 'ggf' ? 'optional' : pos.inclusion }}</span>
                                        </td>
                                        <td style="width: 20%" class="text-right fw-bold">
                                          <span v-if="pos.excluded" class="text-muted">—</span>
                                          <template v-else-if="pos.isAuftragsabh">
                                            <div class="d-flex align-items-center justify-content-end">
                                              <input type="number" step="0.01" min="0" class="form-control form-control-sm dm-manual-price" v-model.number="manualPrices[pos._idx]" placeholder="Preis" @input="onManualPriceInput" />
                                              <span class="ml-1 small text-muted">EUR</span>
                                            </div>
                                          </template>
                                          <span v-else-if="typeof pos.calculatedPrice === 'number'">{{ formatPrice(pos.calculatedPrice) }} EUR</span>
                                          <span v-else class="text-muted small">{{ pos.calculatedPrice }}</span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>

                            <!-- INDIVIDUELL MODE -->
                            <div v-show="mode === 'individuell'">
                              <div v-if="!wtg" class="alert alert-info">{{ $t('dm_kalk_select_wtg_first') }}</div>
                              <div v-if="wtg">
                                <div class="d-flex align-items-center mb-4 pb-3" style="border-bottom: 1px solid rgba(128, 128, 128, 0.15); gap: 12px;">
                                  <label class="form-label mb-0 text-muted small text-nowrap">{{ $t('dm_kalk_label_vorlage') }}:</label>
                                  <select class="form-control form-control-sm" style="width: 200px;" v-model="indVorlage" @change="applyVorlage">
                                    <option value="">-- {{ $t('dm_kalk_label_keine') }} --</option>
                                    <option value="ibn">IBN</option>
                                    <option value="eow">EoW</option>
                                    <option value="wkp_basic">WKP-basic</option>
                                    <option value="wkp_plus">WKP-Plus</option>
                                    <option value="zop">ZOP</option>
                                  </select>
                                  <button class="btn btn-sm btn-outline-secondary" @click="clearIndividuell">{{ $t('dm_kalk_btn_reset') }}</button>
                                </div>
                                <div v-for="(group, gIdx) in individuellGroups" :key="gIdx" class="mb-4">
                                  <div class="dm-group-header-sm" @click="group.collapsed = !group.collapsed" style="cursor: pointer;">
                                    <em class="icon ni" :class="group.collapsed ? 'ni-chevron-right' : 'ni-chevron-down'"></em>
                                    <strong>{{ group.produkt }}</strong>
                                    <span v-if="countChecked(group) > 0" class="dm-checked-count">{{ countChecked(group) }}/{{ group.items.length }}</span>
                                    <span class="float-right text-primary fw-bold">{{ formatPrice(group.subtotal) }} EUR</span>
                                  </div>
                                  <table v-show="!group.collapsed" class="dm-table dm-table-compact">
                                    <tbody>
                                      <tr v-for="(pos, pIdx) in group.items" :key="pIdx" :class="{ 'bg-light': pos.checked }">
                                        <td style="width: 5%">
                                          <div class="custom-control custom-checkbox">
                                            <input type="checkbox" class="custom-control-input" :id="'ind_' + gIdx + '_' + pIdx" v-model="pos.checked" />
                                            <label class="custom-control-label" :for="'ind_' + gIdx + '_' + pIdx"></label>
                                          </div>
                                        </td>
                                        <td style="width: 35%">{{ pos.modul }}</td>
                                        <td style="width: 35%" class="text-muted small">{{ pos.detail }}</td>
                                        <td style="width: 25%" class="text-right fw-bold" @dblclick="startPriceEdit(pos, gIdx, pIdx)">
                                          <template v-if="pos.editingPrice">
                                            <div class="d-flex align-items-center justify-content-end">
                                              <input type="number" step="0.01" min="0" class="form-control form-control-sm dm-manual-price" v-model.number="pos.overridePrice" :ref="'priceInput_' + gIdx + '_' + pIdx" placeholder="Preis" @blur="stopPriceEdit(pos)" @keyup.enter="stopPriceEdit(pos)" @keyup.escape="cancelPriceEdit(pos)" />
                                              <span class="ml-1 small text-muted">EUR</span>
                                            </div>
                                          </template>
                                          <template v-else-if="pos.checked && pos.isAuftragsabh && !pos.overridePrice">
                                            <div class="d-flex align-items-center justify-content-end">
                                              <input type="number" step="0.01" min="0" class="form-control form-control-sm dm-manual-price" v-model.number="pos.manualPrice" placeholder="Preis" />
                                              <span class="ml-1 small text-muted">EUR</span>
                                            </div>
                                          </template>
                                          <template v-else-if="pos.checked">
                                            <span :class="{ 'dm-price-override': pos.overridePrice > 0 }" :title="pos.overridePrice > 0 ? 'Doppelklick zum Bearbeiten (Original: ' + formatPrice(pos._origPrice) + ' EUR)' : 'Doppelklick für eigenen Preis'">
                                              {{ formatPrice(pos.overridePrice > 0 ? pos.overridePrice : pos.calculatedPrice) }} EUR
                                            </span>
                                            <a v-if="pos.overridePrice > 0" href="#" class="ml-1 text-muted small" @click.prevent="resetOverride(pos)" title="Zurücksetzen">&times;</a>
                                          </template>
                                          <span v-else class="text-muted">—</span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>

                          </div>
                        </div>

                        <!-- Nebenkosten Section -->
                        <div class="card card-bordered mt-4" v-if="wtg">
                          <div class="card-inner">
                            <h6 class="overline-title overline-title-alt mb-4">{{ $t('dm_pl_tab_nebenkosten') }}</h6>
                            <div class="row g-4">
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_km_hin') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.km_hin" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_km_rueck') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.km_rueck" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_reisezeit_hin') }}</label>
                                  <input type="number" min="0" step="0.5" class="form-control" v-model.number="nk.reisezeit_hin" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_reisezeit_rueck') }}</label>
                                  <input type="number" min="0" step="0.5" class="form-control" v-model.number="nk.reisezeit_rueck" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_uebernachtungen') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.uebernachtungen" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_ganztage') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.ganztage" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_halbtage') }}</label>
                                  <input type="number" min="0" class="form-control" v-model.number="nk.halbtage" />
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="form-group">
                                  <label class="form-label">{{ $t('dm_kalk_label_personen') }} / {{ $t('dm_kalk_label_autos') }}</label>
                                  <div class="d-flex" style="gap: 8px;">
                                    <input type="number" min="1" class="form-control" v-model.number="nk.personen" :placeholder="$t('dm_kalk_label_personen')" />
                                    <input type="number" min="1" class="form-control" v-model.number="nk.autos" :placeholder="$t('dm_kalk_label_autos')" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Total Summary (sticky) -->
                        <div class="dm-summary-sticky" v-if="wtg && (selectedPruefTyp || hasActiveZusatz || mode === 'individuell')">
                          <div class="dm-summary-bar">
                            <div class="dm-summary-items">
                              <div class="dm-summary-item">
                                <span class="dm-summary-label">{{ $t('dm_kalk_label_pruefpreis') }}</span>
                                <span class="dm-summary-value">{{ formatPrice(pruefPreis) }}</span>
                              </div>
                              <div class="dm-summary-item" v-if="anzahlWEA > 1">
                                <span class="dm-summary-label">&times; {{ anzahlWEA }} WEA</span>
                                <span class="dm-summary-value">{{ formatPrice(pruefPreis * anzahlWEA) }}</span>
                              </div>
                              <div class="dm-summary-item">
                                <span class="dm-summary-label">{{ $t('dm_kalk_label_rabatt') }} <small>({{ discountLabel }})</small></span>
                                <span class="dm-summary-value" :class="{ 'text-success': discountAmount < 0, 'text-danger': discountAmount > 0 }">{{ discountAmount >= 0 ? '+' : '' }}{{ formatPrice(discountAmount) }}</span>
                              </div>
                              <div class="dm-summary-item" @click="showNkDetail = !showNkDetail" style="cursor: pointer;">
                                <span class="dm-summary-label">{{ $t('dm_pl_tab_nebenkosten') }} <em class="icon ni ni-info small" style="opacity: 0.5;"></em></span>
                                <span class="dm-summary-value">{{ formatPrice(nebenkostenTotal) }}</span>
                              </div>
                            </div>
                            <div class="dm-summary-total">
                              <span class="dm-summary-total-label">{{ $t('dm_kalk_label_gesamt') }}</span>
                              <span class="dm-summary-total-value">{{ formatPrice(gesamtPreis) }} EUR</span>
                              <span class="dm-summary-netto">{{ $t('dm_kalk_label_gesamt_netto') }}</span>
                              <button class="btn btn-sm btn-outline-primary mt-2" :disabled="angebotsLoading" @click="generateAngebot">
                                <span v-if="angebotsLoading" class="spinner-border spinner-border-sm mr-1"></span>
                                <em v-else class="icon ni ni-file-pdf mr-1"></em>
                                {{ $t('dm_kalk_btn_angebot') }}
                              </button>
                            </div>
                          </div>
                          <!-- Nebenkosten detail breakdown -->
                          <div class="dm-nk-detail" v-if="showNkDetail && nebenkostenTotal > 0">
                            <table class="dm-nk-detail-table">
                              <tbody>
                                <tr v-if="nkBreakdown.km > 0">
                                  <td>Km-Pauschale ({{ nk.km_hin + nk.km_rueck }} km &times; {{ nk.autos }} Auto{{ nk.autos > 1 ? 's' : '' }})</td>
                                  <td class="text-right">{{ formatPrice(nkBreakdown.km) }} EUR</td>
                                </tr>
                                <tr v-if="nkBreakdown.reisezeit > 0">
                                  <td>Reisezeit ({{ nk.reisezeit_hin + nk.reisezeit_rueck }} h &times; {{ nk.autos }} Auto{{ nk.autos > 1 ? 's' : '' }})</td>
                                  <td class="text-right">{{ formatPrice(nkBreakdown.reisezeit) }} EUR</td>
                                </tr>
                                <tr v-if="nkBreakdown.uebernachtung > 0">
                                  <td>Übernachtung ({{ nk.uebernachtungen }} N. &times; {{ nk.personen }} Pers.)</td>
                                  <td class="text-right">{{ formatPrice(nkBreakdown.uebernachtung) }} EUR</td>
                                </tr>
                                <tr v-if="nkBreakdown.verpflegung > 0">
                                  <td>Verpflegung ({{ nk.ganztage }} Ganzt. + {{ nk.halbtage }} Halbt. &times; {{ nk.personen }} Pers.)</td>
                                  <td class="text-right">{{ formatPrice(nkBreakdown.verpflegung) }} EUR</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { getPreislisteList, getPreisliste, kalkulatorAiParse, kalkulatorAngebotPdf, getAdressenList, getParksList } from "@/services/dm.service";

export default {
  name: "DmKalkulator",
  components: { Header, Navigation, Footer },
  mixins: [dmPermissionsMixin],
  data() {
    return {
      preisliste: null,
      loading: false,
      mode: "standard",
      // Kunde / Park
      adressen: [],
      parks: [],
      selectedKundeId: "",
      selectedParkId: "",
      projektName: "",
      angebotsLoading: false,
      showKunde: false,
      // WTG selection
      selectedHersteller: "",
      selectedTyp: "",
      selectedNH: 0,
      anzahlWEA: 1,
      // Standard mode
      selectedPruefTyp: "",
      exclTrafo: false,
      begleitpersonProvided: false,
      excludedPositions: {},  // key: berechnung index, value: true if excluded
      manualPrices: {},       // key: berechnung index, value: manually entered price for auftragsabh. positions
      manualPricesTick: 0,    // reactivity trigger
      zusatzProdukte: [
        { key: "swm", label: "Schwingungsmessung (SWM)", active: false, selected: "", produktMatch: "swm" },
        { key: "swa", label: "Schwingungsanalyse (SWA)", active: false, selected: "", produktMatch: "swa" },
        { key: "thermo", label: "Thermografie", active: false, selected: "", produktMatch: "thermografie" },
        { key: "ve", label: "Videoendoskopie (VE)", active: false, selected: "", produktMatch: "ve" },
        { key: "dguv", label: "DGUV V3", active: false, selected: "", produktMatch: "dguv" },
        { key: "rbwinkel", label: "Blattwinkelvermessung", active: false, selected: "", produktMatch: "rb-winkel" },
        { key: "vtb", label: "Vertikale Turmbewegung (VTB)", active: false, selected: "", produktMatch: "vtb" },
      ],
      // Individuell mode
      indVorlage: "",
      individuellItems: [],
      // AI Assistent
      aiText: "",
      aiLoading: false,
      aiError: "",
      aiUsage: null,
      indTouched: false,  // true once user has been in individuell mode
      showNkDetail: false,
      // Nebenkosten
      nk: {
        km_hin: 0, km_rueck: 0,
        reisezeit_hin: 0, reisezeit_rueck: 0,
        uebernachtungen: 0,
        ganztage: 0, halbtage: 0,
        personen: 1, autos: 1,
      },
    };
  },
  computed: {
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page === "setup";
    },
    selectedKunde() {
      if (!this.selectedKundeId) return null;
      return this.adressen.find(function(a) { return a._id === this.selectedKundeId; }.bind(this)) || null;
    },
    selectedPark() {
      if (!this.selectedParkId) return null;
      return this.parks.find(function(p) { return p._id === this.selectedParkId; }.bind(this)) || null;
    },
    herstellerList() {
      if (!this.preisliste || !this.preisliste.dropdowns) return [];
      return this.preisliste.dropdowns.hersteller || [];
    },
    filteredTypen() {
      if (!this.preisliste || !this.selectedHersteller) return [];
      return (this.preisliste.anlagendaten || []).filter(function(a) { return a.hersteller === this.selectedHersteller; }.bind(this));
    },
    wtg() {
      if (!this.selectedTyp || !this.preisliste) return null;
      var found = (this.preisliste.anlagendaten || []).find(function(a) { return a.typ === this.selectedTyp; }.bind(this));
      return found || null;
    },
    wtgNabenhoehen() {
      return this.wtg ? (this.wtg.nabenhoehen || []) : [];
    },
    // Build dropdown options per Zusatzprodukt from berechnung data
    zusatzOptionen() {
      if (!this.preisliste || !this.preisliste.berechnung) return {};
      var result = {};
      var berechnung = this.preisliste.berechnung;
      for (var z = 0; z < this.zusatzProdukte.length; z++) {
        var zp = this.zusatzProdukte[z];
        var match = zp.produktMatch.toLowerCase();
        var options = [];
        var seen = {};
        for (var i = 0; i < berechnung.length; i++) {
          var pos = berechnung[i];
          var produkt = (pos.produkt || "").toLowerCase().trim();
          // Match: "swm" matches "SWM ", "ve" matches "VE", "dguv" matches "DGUV V3", etc.
          var isMatch = false;
          if (match === "thermografie") isMatch = produkt.indexOf("thermografie") > -1;
          else if (match === "rb-winkel") isMatch = produkt.indexOf("rb-winkel") > -1 || produkt.indexOf("winkel") > -1;
          else isMatch = produkt.indexOf(match) > -1;
          if (isMatch) {
            var detail = (pos.detail || "").trim();
            var label = detail || (pos.modul || "").trim();
            if (!label || label === "[Platzhalter]") continue;
            var key = label.toLowerCase();
            if (seen[key]) continue;
            seen[key] = true;
            options.push({ value: "" + i, label: label });
          }
        }
        result[zp.key] = options;
      }
      return result;
    },
    stundensatz() {
      return (this.preisliste && this.preisliste.berechnungs_saetze) ? this.preisliste.berechnungs_saetze.stundensatz : 116;
    },
    tagessatz() {
      return (this.preisliste && this.preisliste.berechnungs_saetze) ? this.preisliste.berechnungs_saetze.tagessatz : 935;
    },
    nkSaetze() {
      return (this.preisliste && this.preisliste.nebenkosten_saetze) || {
        verpflegung_ganztag: 28, verpflegung_halbtag: 14,
        reisezeit_pro_h: 79, uebernachtung_pro_nacht: 120, km_pauschale: 0.49,
      };
    },
    hasActiveZusatz() {
      return this.zusatzProdukte.some(function(zp) { return zp.active; });
    },
    // Standard mode: filter berechnung by pruefungstyp + active Zusatzprodukte
    standardGroups() {
      if (!this.wtg || !this.preisliste) return [];
      if (!this.selectedPruefTyp && !this.hasActiveZusatz) return [];
      var self = this;
      var berechnung = this.preisliste.berechnung || [];
      var pt = this.selectedPruefTyp;
      var included = [];

      // Build set of active Zusatzprodukt matches
      var activeZusatz = {};
      for (var z = 0; z < self.zusatzProdukte.length; z++) {
        if (self.zusatzProdukte[z].active) {
          activeZusatz[self.zusatzProdukte[z].produktMatch] = self.zusatzProdukte[z];
        }
      }

      for (var i = 0; i < berechnung.length; i++) {
        var pos = berechnung[i];
        var modul = (pos.modul || "").toLowerCase();
        var inc = "";

        // 1. Hauptprüfung: match via pruefungstypen columns
        if (pt && pos.pruefungstypen) {
          var ptInc = pos.pruefungstypen[pt] || "";
          if (ptInc === "x" || ptInc === "ggf") {
            // Exclusion logic
            if (self.exclTrafo && modul.indexOf("trafo") > -1) continue;
            if (self.begleitpersonProvided && modul.indexOf("begleitperson") > -1) continue;
            inc = ptInc;
          }
        }

        // 2. Zusatzprodukte: match by berechnung index (selected value = index in berechnung array)
        if (!inc) {
          for (var zKey in activeZusatz) {
            var zp = activeZusatz[zKey];
            var opts = self.zusatzOptionen[zp.key] || [];
            if (opts.length <= 1) {
              // No sub-options (e.g. RB-Winkel, VTB) — include all matching positions
              for (var o = 0; o < opts.length; o++) {
                if (parseInt(opts[o].value) === i) { inc = "x"; break; }
              }
            } else {
              // Has sub-options — only include the selected one
              if (zp.selected && parseInt(zp.selected) === i) inc = "x";
            }
          }
        }

        if (inc) {
          var price = self.calcPrice(pos);
          var isAuftr = price === "auftragsabh.";
          if (isAuftr && self.manualPrices[i] > 0 && self.manualPricesTick >= 0) {
            price = self.manualPrices[i];
          }
          included.push({
            produkt: pos.produkt,
            modul: pos.modul,
            detail: pos.detail,
            inclusion: inc,
            _idx: i,
            excluded: !!self.excludedPositions[i],
            isAuftragsabh: isAuftr,
            calculatedPrice: price,
          });
        }
      }
      return self.groupByProdukt(included);
    },
    // Individuell mode: all positions with checkboxes
    individuellGroups() {
      if (!this.wtg) return [];
      var self = this;
      var groups = [];
      var map = {};
      for (var i = 0; i < this.individuellItems.length; i++) {
        var item = this.individuellItems[i];
        var cp = self.calcPrice(item._src);
        // For auftragsabh: use manual price if entered
        if (cp === "auftragsabh." && item.manualPrice > 0) {
          cp = item.manualPrice;
        }
        item.calculatedPrice = cp;
        item._origPrice = cp;
        var key = item.produkt;
        if (!map[key]) {
          map[key] = { produkt: key, collapsed: false, items: [], subtotal: 0 };
          groups.push(map[key]);
        }
        map[key].items.push(item);
        var effectivePrice = item.overridePrice > 0 ? item.overridePrice : item.calculatedPrice;
        if (item.checked && typeof effectivePrice === "number") {
          map[key].subtotal += effectivePrice;
        }
      }
      return groups;
    },
    // Price calculations
    pruefPreis() {
      if (this.mode === "standard") {
        var total = 0;
        for (var g = 0; g < this.standardGroups.length; g++) {
          total += this.standardGroups[g].subtotal;
        }
        return total;
      } else {
        var total2 = 0;
        for (var g2 = 0; g2 < this.individuellGroups.length; g2++) {
          total2 += this.individuellGroups[g2].subtotal;
        }
        return total2;
      }
    },
    discountFactor() {
      var n = this.anzahlWEA;
      if (n <= 1) return 1.05;
      if (n <= 3) return 1.0;
      if (n <= 7) return 0.95;
      return 0.90;
    },
    discountLabel() {
      var f = this.discountFactor;
      if (f > 1) return "+" + Math.round((f - 1) * 100) + "% (1 WEA)";
      if (f === 1) return "0%";
      return "-" + Math.round((1 - f) * 100) + "%";
    },
    discountAmount() {
      var base = this.pruefPreis * this.anzahlWEA;
      return base * this.discountFactor - base;
    },
    nkBreakdown() {
      var s = this.nkSaetze;
      var nk = this.nk;
      return {
        verpflegung: (nk.ganztage * s.verpflegung_ganztag + nk.halbtage * s.verpflegung_halbtag) * nk.personen,
        reisezeit: s.reisezeit_pro_h * (nk.reisezeit_hin + nk.reisezeit_rueck) * nk.autos,
        uebernachtung: s.uebernachtung_pro_nacht * nk.uebernachtungen * nk.personen,
        km: s.km_pauschale * (nk.km_hin + nk.km_rueck) * nk.autos,
      };
    },
    nebenkostenTotal() {
      var b = this.nkBreakdown;
      return b.verpflegung + b.reisezeit + b.uebernachtung + b.km;
    },
    gesamtPreis() {
      var base = this.pruefPreis * this.anzahlWEA;
      return base * this.discountFactor + this.nebenkostenTotal;
    },
  },
  watch: {
    "preisliste.berechnung": function() { this.buildIndividuellItems(); },
    mode: function(newMode) {
      if (newMode === "individuell" && !this.indTouched && this.standardGroups.length > 0) {
        this.transferStandardToIndividuell();
      }
      if (newMode === "individuell") {
        this.indTouched = true;
      }
    },
  },
  created() {
    this.loadPreisliste();
    this.loadStammdaten();
  },
  methods: {
    autoResizeAi: function(evt) {
      var el = evt.target;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    },
    runAiParse: function() {
      if (!this.aiText.trim() || this.aiLoading) return;
      var self = this;
      self.aiError = "";
      self.aiLoading = true;
      self.aiUsage = null;

      kalkulatorAiParse(self.aiText, self.preisliste._id)
        .then(function(d) {
          self.aiLoading = false;
          if (d && d.status === "error") {
            self.aiError = d.message || "AI-Fehler";
            return;
          }
          var result = (d && d.data && d.data.result) || d.data || {};
          self.aiUsage = (d && d.data && d.data.usage) || null;
          self.applyAiResult(result);
          self.aiText = "";
        })
        .catch(function(e) {
          self.aiLoading = false;
          self.aiError = (e && e.message) || "Fehler bei der AI-Verarbeitung";
        });
    },
    applyAiResult: function(r) {
      // 1. Hersteller
      if (r.hersteller && this.herstellerList.indexOf(r.hersteller) > -1) {
        this.selectedHersteller = r.hersteller;
      }

      // 2. Typ (needs hersteller to be set first for filteredTypen)
      if (r.typ) {
        var matchTyp = this.filteredTypen.find(function(t) { return t.typ === r.typ; });
        if (matchTyp) {
          this.selectedTyp = matchTyp.typ;
          this.onTypChange();
        }
      }

      // 3. Nabenhöhe
      if (r.nabenhoehe && this.wtgNabenhoehen.indexOf(r.nabenhoehe) > -1) {
        this.selectedNH = r.nabenhoehe;
      }

      // 4. Anzahl WEA
      if (r.anzahl_wea && r.anzahl_wea > 0) {
        this.anzahlWEA = r.anzahl_wea;
      }

      // 5. Prüfungstyp + Modus
      if (r.pruef_typ) {
        this.mode = "standard";
        this.selectedPruefTyp = r.pruef_typ;
      }

      // 6. Zusatzprodukte
      if (r.zusatz && r.zusatz.length) {
        for (var z = 0; z < this.zusatzProdukte.length; z++) {
          var zp = this.zusatzProdukte[z];
          zp.active = r.zusatz.indexOf(zp.key) > -1;
        }
      }

      // 7. Optionen
      if (r.excl_trafo) this.exclTrafo = true;
      if (r.begleitperson_provided) this.begleitpersonProvided = true;

      // 8. Nebenkosten
      if (r.nebenkosten) {
        var nk = r.nebenkosten;
        if (nk.km_hin != null) this.nk.km_hin = nk.km_hin;
        if (nk.km_rueck != null) this.nk.km_rueck = nk.km_rueck;
        if (nk.reisezeit_hin != null) this.nk.reisezeit_hin = nk.reisezeit_hin;
        if (nk.reisezeit_rueck != null) this.nk.reisezeit_rueck = nk.reisezeit_rueck;
        if (nk.uebernachtungen != null) this.nk.uebernachtungen = nk.uebernachtungen;
        if (nk.ganztage != null) this.nk.ganztage = nk.ganztage;
        if (nk.halbtage != null) this.nk.halbtage = nk.halbtage;
        if (nk.personen != null) this.nk.personen = nk.personen;
        if (nk.autos != null) this.nk.autos = nk.autos;
      }

      // 9. Individuell-Modus: einzelne Positionen
      if (r.mode === "individuell" && r.positionen && r.positionen.length) {
        this.mode = "individuell";
        // Reset all
        for (var i = 0; i < this.individuellItems.length; i++) {
          this.individuellItems[i].checked = false;
        }
        // Check matched positions by index
        for (var p = 0; p < r.positionen.length; p++) {
          var idx = r.positionen[p];
          if (idx >= 0 && idx < this.individuellItems.length) {
            this.individuellItems[idx].checked = true;
          }
        }
      }
    },
    loadStammdaten: function() {
      var self = this;
      getAdressenList().then(function(d) {
        self.adressen = (d && d.data && d.data.list) || [];
      }).catch(function() {});
      getParksList().then(function(d) {
        self.parks = (d && d.data && d.data.list) || [];
      }).catch(function() {});
    },
    onKundeChange: function() {
      // Could auto-fill project name from customer
    },
    generateAngebot: function() {
      var self = this;
      if (!self.wtg) return;
      self.angebotsLoading = true;

      // Build positions list from current mode
      var positionen = [];
      if (self.mode === "standard") {
        for (var g = 0; g < self.standardGroups.length; g++) {
          for (var p = 0; p < self.standardGroups[g].items.length; p++) {
            var pos = self.standardGroups[g].items[p];
            if (!pos.excluded && typeof pos.calculatedPrice === "number") {
              positionen.push({
                produkt: pos.produkt,
                modul: pos.modul,
                detail: pos.detail,
                preis: pos.calculatedPrice,
              });
            }
          }
        }
      } else {
        for (var g2 = 0; g2 < self.individuellGroups.length; g2++) {
          for (var p2 = 0; p2 < self.individuellGroups[g2].items.length; p2++) {
            var item = self.individuellGroups[g2].items[p2];
            if (item.checked) {
              var preis = item.overridePrice > 0 ? item.overridePrice : item.calculatedPrice;
              if (typeof preis === "number") {
                positionen.push({
                  produkt: item.produkt,
                  modul: item.modul,
                  detail: item.detail,
                  preis: preis,
                });
              }
            }
          }
        }
      }

      var payload = {
        preisliste_id: self.preisliste._id,
        kunde_id: self.selectedKundeId || undefined,
        park_id: self.selectedParkId || undefined,
        projekt_name: self.projektName || undefined,
        wtg: {
          hersteller: self.selectedHersteller,
          typ: self.selectedTyp,
          nabenhoehe: self.selectedNH,
          durchmesser: self.wtg.durchmesser,
          kw: self.wtg.kw,
        },
        anzahl_wea: self.anzahlWEA,
        positionen: positionen,
        pruef_preis: self.pruefPreis,
        discount_factor: self.discountFactor,
        discount_amount: self.discountAmount,
        nebenkosten: self.nkBreakdown,
        nebenkosten_total: self.nebenkostenTotal,
        gesamt_preis: self.gesamtPreis,
        nk_eingaben: self.nk,
      };

      kalkulatorAngebotPdf(payload)
        .then(function(resp) {
          self.angebotsLoading = false;
          var blob = new Blob([resp.data], { type: "application/pdf" });
          var link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "Angebot_" + (self.projektName || self.selectedTyp || "Kalkulation") + ".pdf";
          link.click();
          window.URL.revokeObjectURL(link.href);
        })
        .catch(function(e) {
          self.angebotsLoading = false;
          self.$store.dispatch("api_error", e);
        });
    },
    loadPreisliste() {
      var self = this;
      self.loading = true;
      self.$store.commit("set_loading_page", "kalkulator");
      getPreislisteList()
        .then(function(d) {
          var list = (d.data && d.data.list) || [];
          if (list.length > 0) {
            // Load the first (= active) preisliste
            return getPreisliste(list[0]._id);
          }
          self.$store.commit("set_loading_page", null);
          self.loading = false;
        })
        .then(function(d) {
          self.$store.commit("set_loading_page", null);
          self.loading = false;
          if (d && d.data && d.data.preisliste) {
            self.preisliste = d.data.preisliste;
            self.buildIndividuellItems();
          }
        })
        .catch(function(e) {
          self.$store.commit("set_loading_page", null);
          self.loading = false;
          self.$store.dispatch("api_error", e);
        });
    },
    loadPreislisteDetail(id) {
      var self = this;
      self.$store.commit("set_loading_page", "kalkulator");
      getPreisliste(id)
        .then(function(d) {
          self.$store.commit("set_loading_page", null);
          if (d && d.data && d.data.preisliste) {
            self.preisliste = d.data.preisliste;
            self.buildIndividuellItems();
          }
        })
        .catch(function(e) {
          self.$store.commit("set_loading_page", null);
          self.$store.dispatch("api_error", e);
        });
    },
    onHerstellerChange() {
      this.selectedTyp = "";
      this.selectedNH = 0;
    },
    onTypChange() {
      this.selectedNH = 0;
      // Auto-select first Nabenhoehe if only one
      if (this.wtg && this.wtg.nabenhoehen && this.wtg.nabenhoehen.length === 1) {
        this.selectedNH = this.wtg.nabenhoehen[0];
      }
    },
    buildIndividuellItems() {
      if (!this.preisliste || !this.preisliste.berechnung) return;
      this.individuellItems = this.preisliste.berechnung.map(function(pos) {
        var isAuftr = (pos.formel_typ === "auftragsabhaengig") || (pos.preis_typ === "auftragsabhaengig") || (typeof pos.preis === "string" && pos.preis.indexOf("auftrags") > -1);
        return {
          produkt: pos.produkt,
          modul: pos.modul,
          detail: pos.detail,
          checked: false,
          calculatedPrice: 0,
          manualPrice: 0,
          overridePrice: 0,
          editingPrice: false,
          _origPrice: 0,
          isAuftragsabh: isAuftr,
          _src: pos,
        };
      });
      // Auto-select first option for each Zusatzprodukt
      for (var z = 0; z < this.zusatzProdukte.length; z++) {
        var zp = this.zusatzProdukte[z];
        var opts = this.zusatzOptionen[zp.key] || [];
        if (opts.length > 0 && !zp.selected) {
          zp.selected = opts[0].value;
        }
      }
    },
    applyVorlage: function() {
      var pt = this.indVorlage;
      if (!pt || !this.individuellItems.length) return;
      for (var i = 0; i < this.individuellItems.length; i++) {
        var item = this.individuellItems[i];
        var src = item._src;
        var inc = src.pruefungstypen ? (src.pruefungstypen[pt] || "") : "";
        item.checked = (inc === "x" || inc === "ggf");
      }
    },
    clearIndividuell: function() {
      this.indVorlage = "";
      this.indTouched = false;
      for (var i = 0; i < this.individuellItems.length; i++) {
        this.individuellItems[i].checked = false;
        this.individuellItems[i].manualPrice = 0;
        this.individuellItems[i].overridePrice = 0;
        this.individuellItems[i].editingPrice = false;
      }
    },
    startPriceEdit: function(pos, gIdx, pIdx) {
      if (!pos.checked) return;
      this.$set(pos, "editingPrice", true);
      if (!pos.overridePrice) {
        var current = typeof pos.calculatedPrice === "number" ? pos.calculatedPrice : 0;
        this.$set(pos, "overridePrice", current);
      }
      var refKey = "priceInput_" + gIdx + "_" + pIdx;
      var self = this;
      this.$nextTick(function() {
        var el = self.$refs[refKey];
        if (el && el[0]) { el[0].focus(); el[0].select(); }
      });
    },
    stopPriceEdit: function(pos) {
      this.$set(pos, "editingPrice", false);
      if (pos.overridePrice <= 0) {
        this.$set(pos, "overridePrice", 0);
      }
    },
    cancelPriceEdit: function(pos) {
      this.$set(pos, "overridePrice", 0);
      this.$set(pos, "editingPrice", false);
    },
    resetOverride: function(pos) {
      this.$set(pos, "overridePrice", 0);
    },
    transferStandardToIndividuell: function() {
      // Collect _idx of all non-excluded standard positions
      var activeIdxs = {};
      for (var g = 0; g < this.standardGroups.length; g++) {
        for (var p = 0; p < this.standardGroups[g].items.length; p++) {
          var pos = this.standardGroups[g].items[p];
          if (!pos.excluded) {
            activeIdxs[pos._idx] = true;
          }
        }
      }
      // Apply to individuell items (berechnung index matches)
      for (var i = 0; i < this.individuellItems.length; i++) {
        this.individuellItems[i].checked = !!activeIdxs[i];
      }
    },
    countChecked: function(group) {
      var c = 0;
      for (var i = 0; i < group.items.length; i++) {
        if (group.items[i].checked) c++;
      }
      return c;
    },
    onManualPriceInput: function() {
      this.manualPricesTick++;
    },
    toggleExcluded: function(idx) {
      this.$set(this.excludedPositions, idx, !this.excludedPositions[idx]);
    },
    groupByProdukt: function(items) {
      var groups = [];
      var map = {};
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var key = item.produkt;
        if (!map[key]) {
          map[key] = { produkt: key, items: [], subtotal: 0 };
          groups.push(map[key]);
        }
        map[key].items.push(item);
        if (!item.excluded && typeof item.calculatedPrice === "number") {
          map[key].subtotal += item.calculatedPrice;
        }
      }
      return groups;
    },
    /**
     * Data-driven price calculation engine.
     * Reads formel_typ, formel_faktor, formel_basis from each position.
     * Falls back to legacy hardcoded logic if formel_typ is not set.
     */
    calcPrice: function(pos) {
      if (!pos || !this.wtg) return 0;
      var w = this.wtg;
      var nh = this.selectedNH || (w.nabenhoehen && w.nabenhoehen[0]) || 0;
      var rd = w.durchmesser || 0;
      var kw = w.kw || 0;
      var satz = this.stundensatz;
      var typ = pos.formel_typ || "";
      var faktor = pos.formel_faktor || 0;
      var basis = pos.formel_basis || 0;

      // Data-driven calculation
      switch (typ) {
        case "festpreis":
          return basis || pos.preis || 0;
        case "stundensatz_faktor":
          return Math.round(faktor * satz * 100) / 100;
        case "nh_faktor":
          return Math.round(faktor * nh * 100) / 100;
        case "rd_faktor":
          return Math.round(faktor * rd * 100) / 100;
        case "basis_plus_rd":
          return Math.round((basis + faktor * rd) * 100) / 100;
        case "basis_plus_nh":
          return Math.round((basis + faktor * nh) * 100) / 100;
        case "pruefzeit_stundensatz":
          return Math.round(w.pruefzeit * satz * (faktor || 1) * 100) / 100;
        case "anlagendaten":
          // Resolve field from detail text
          var det = (pos.detail || pos.modul || "").toLowerCase();
          if (det.indexOf("innen") > -1) return w.festpreis_rb_innen || 0;
          if (det.indexOf("au") > -1 && det.indexOf("rotorbl") > -1) return w.festpreis_rb_aussen || 0;
          return w.blitzschutz_pauschale || 0;
        case "kw_staffel":
          // basis = Grundpreis, faktor = Zuschlag-Schwelle (vereinfacht)
          var p = basis;
          if (kw > 2500) p += faktor;
          if (kw > 3500) p += faktor;
          if (kw > 5000) p += faktor;
          return p;
        case "einzelpreis_staffel":
          var ep = (this.preisliste.einzelpreise || [])[0];
          if (ep && ep.staffeln) {
            var staffel = ep.staffeln.find(function(s) { return s.anzahl === this.anzahlWEA; }.bind(this));
            if (!staffel) staffel = ep.staffeln[ep.staffeln.length - 1];
            if (staffel) {
              var sp = staffel.basispreis;
              if (nh >= 130) sp += staffel.zuschlaege.nh_ab_130;
              else if (nh >= 100) sp += staffel.zuschlaege.nh_ab_100;
              if (rd >= 150) sp += staffel.zuschlaege.rd_ab_150;
              else if (rd >= 130) sp += staffel.zuschlaege.rd_ab_130;
              else if (rd >= 100) sp += staffel.zuschlaege.rd_ab_100;
              return sp;
            }
          }
          return basis || 0;
        case "auftragsabhaengig":
          return "auftragsabh.";
        default:
          // Legacy fallback when formel_typ is not set
          if (pos.preis_typ === "auftragsabhaengig" || (typeof pos.preis === "string" && pos.preis.indexOf("auftrags") > -1)) {
            return "auftragsabh.";
          }
          if (pos.preis > 0) return pos.preis;
          return this.calcPriceLegacy(pos, w, nh, rd, kw, satz);
      }
    },
    /**
     * Legacy fallback: text-based price detection when formel_typ is not set.
     * Will be unused once the import sets formel_typ on all positions.
     */
    calcPriceLegacy: function(pos, w, nh, rd, kw, satz) {
      var modul = (pos.modul || "").toLowerCase();
      var detail = (pos.detail || "").toLowerCase();
      var produkt = (pos.produkt || "").toLowerCase().trim();

      // TP Maschine
      if (produkt === "tp maschine") {
        if (modul.indexOf("maschinenhaus") > -1 && modul.indexOf("elektrotechnik") > -1) return Math.round(w.pruefzeit * satz * 100) / 100;
        if (detail.indexOf("sichtinspektion getriebe") > -1) return w.getriebe === "n" ? 0 : 0.75 * satz;
        if (modul.indexOf("trafo") > -1) {
          var tp = (w.trafo_position || "").toLowerCase();
          if (tp === "external") return 0.25 * satz;
          if (tp === "tower") return 0.5 * satz;
          return 0.75 * satz;
        }
        if (modul.indexOf("dokumenten") > -1) return detail.indexOf("vorpr") > -1 ? 0.25 * satz : 0.5 * satz;
        if (modul.indexOf("turm") > -1 && detail.indexOf("innen") > -1) return 1.5 * nh;
        if (detail.indexOf("hybridturm") > -1) return w.turmtyp === "Hybrid" ? 0.75 * satz : 0;
        if (modul.indexOf("berichtserstellung") > -1) {
          var mhp = w.pruefzeit * satz;
          return detail.indexOf("bescheid") > -1 ? Math.round(mhp * 0.84 * 100) / 100 : Math.round(mhp * 1.2 * 100) / 100;
        }
        if (modul.indexOf("begleitperson") > -1) return detail.indexOf("turm") > -1 ? 2 * nh : 110;
        if (detail.indexOf("gittermast") > -1) return w.turmtyp === "Lattice" ? "auftragsabh." : 0;
      }

      // Nebenkosten TP Maschine
      if (produkt.indexOf("nebenkosten") > -1 && modul.indexOf("pauschale") > -1) return 165;

      // TP RB
      if (produkt === "tp rb") {
        if (modul.indexOf("innen") > -1) return w.festpreis_rb_innen || 0;
        if (modul.indexOf("au") > -1 && modul.indexOf("rotorbl") > -1) return w.festpreis_rb_aussen || 0;
        if (modul.indexOf("sichtpr") > -1) return 5 * rd;
        if (detail.indexOf("lang") > -1) return 160;
        if (modul.indexOf("begleitperson") > -1) return rd <= 100 ? 3 * rd : "anlagenspez.";
        if (modul.indexOf("pflichtpauschale") > -1) return 400 + 2 * rd;
        return 0;
      }

      // Allgemein
      if (produkt === "allgemein") return 0.25 * satz;

      // SWM
      if (produkt.indexOf("swm") > -1) {
        if (w.getriebe === "n" || detail.indexOf("direct") > -1 || detail.indexOf("dd") > -1) return 630;
        var swm = 700;
        if (kw > 2500) swm += 50;
        if (kw > 3500) swm += 80;
        if (kw > 5000) swm += 90;
        return swm;
      }

      // SWA
      if (produkt.indexOf("swa") > -1) {
        if (detail.indexOf("online") > -1) return 1680;
        if (w.getriebe === "n" || detail.indexOf("direct") > -1 || detail.indexOf("dd") > -1) return 680;
        var swa = 750;
        if (kw > 2500) swa += 50;
        if (kw > 3500) swa += 80;
        if (kw > 5000) swa += 110;
        return swa;
      }

      // Zusatz
      if (produkt.indexOf("zusatz") > -1 || produkt === "zusatz") {
        if (modul.indexOf("blitzschutz") > -1 && detail.indexOf("rotorbl") > -1) return w.blitzschutz_pauschale || 150;
        if (modul.indexOf("blitzschutz") > -1) return nh <= 100 ? 100 : 150;
        if (modul.indexOf("erdung") > -1) return 250;
        if (modul.indexOf("schmiermittel") > -1) return 250;
        if (modul.indexOf("konformit") > -1 && detail.indexOf("bimsch") > -1) return 300;
        if (modul.indexOf("konformit") > -1 && detail.indexOf("liefervertrag") > -1) return 150;
        if (modul.indexOf("konformit") > -1 && detail.indexOf("typenpr") > -1) return satz;
        if (modul.indexOf("monet") > -1) return 1.5 * this.tagessatz;
        if (modul.indexOf("sichtweitenmess") > -1 || detail.indexOf("sichtweitenmess") > -1) return satz;
      }

      // Pflichtpauschalen
      if (produkt.indexOf("pflicht") > -1) {
        if (modul.indexOf("zugangstechnik") > -1) return 400 + 2 * rd;
        return 0;
      }

      // Thermografie
      if (produkt.indexOf("thermografie") > -1) return detail.indexOf("extern") > -1 ? 290 : 310;

      // VE
      if (produkt === "ve" && modul.indexOf("videoendoskop") > -1) {
        if (w.getriebe !== "y") return 0;
        if (detail.indexOf("1 planet") > -1) return 1295;
        if (detail.indexOf("2 planet") > -1 || detail.indexOf("3 planet") > -1) return 1525;
        return 0;
      }

      // DGUV V3
      if (produkt.indexOf("dguv") > -1) {
        if (kw > 5000 && w.turmtyp === "Hybrid") return 1520;
        if (kw > 5000) return 1200;
        if (kw > 3500) return 1000;
        if (kw > 2000) return 850;
        return 700;
      }

      // RB-Winkel
      if (produkt.indexOf("rb-winkel") > -1 || produkt.indexOf("winkel") > -1) {
        var ep = (this.preisliste.einzelpreise || [])[0];
        if (ep && ep.staffeln && ep.staffeln.length) {
          var staffel = ep.staffeln.find(function(s) { return s.anzahl === this.anzahlWEA; }.bind(this));
          if (!staffel) staffel = ep.staffeln[ep.staffeln.length - 1];
          if (staffel) {
            var sp = staffel.basispreis;
            if (nh >= 130) sp += staffel.zuschlaege.nh_ab_130;
            else if (nh >= 100) sp += staffel.zuschlaege.nh_ab_100;
            if (rd >= 150) sp += staffel.zuschlaege.rd_ab_150;
            else if (rd >= 130) sp += staffel.zuschlaege.rd_ab_130;
            else if (rd >= 100) sp += staffel.zuschlaege.rd_ab_100;
            return sp;
          }
        }
        return 0;
      }

      // VTB
      if (produkt.indexOf("vtb") > -1) return 858;

      return 0;
    },
    formatPrice: function(val) {
      if (val == null || val === "" || typeof val !== "number") return "—";
      return val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.dm-group-header-sm {
  padding: 10px 14px;
  background: rgba(128, 128, 128, 0.08);
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 4px;
  margin-bottom: 4px;
  font-size: 14px;
}

.dm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.dm-table-compact td {
  padding: 8px 10px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.1);
}

.dm-table tbody tr:nth-child(even) {
  background: rgba(128, 128, 128, 0.03);
}

.dm-table tbody tr:hover {
  background: rgba(128, 128, 128, 0.06);
}

.nav-tabs .nav-link {
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;
}

.nav-tabs .nav-link.active {
  color: #E8751A;
}

.text-right {
  text-align: right;
}

.fw-bold {
  font-weight: 600;
}

.bg-light {
  background: rgba(59, 130, 246, 0.08) !important;
}

.dm-wtg-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dm-wtg-badges .badge {
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
}

/* Sticky summary bar */
.dm-summary-sticky {
  position: sticky;
  bottom: 0;
  z-index: 20;
  margin-top: 24px;
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  margin-bottom: -1.5rem;
}

.dm-summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: var(--bg-white, #fff);
  border-top: 2px solid rgba(128, 128, 128, 0.15);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}

.dm-summary-items {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.dm-summary-item {
  display: flex;
  flex-direction: column;
}

.dm-summary-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #888;
  line-height: 1.2;
}

.dm-summary-value {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.dm-summary-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-left: 24px;
  border-left: 2px solid rgba(128, 128, 128, 0.15);
}

.dm-summary-total-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #888;
}

.dm-summary-total-value {
  font-size: 22px;
  font-weight: 700;
  color: #E8751A;
  line-height: 1.2;
}

.dm-summary-netto {
  font-size: 11px;
  color: #aaa;
}

/* Nebenkosten detail dropdown */
.dm-nk-detail {
  padding: 10px 24px 14px;
  background: rgba(128, 128, 128, 0.04);
  border-top: 1px solid rgba(128, 128, 128, 0.1);
}

.dm-nk-detail-table {
  width: 100%;
  max-width: 500px;
  margin-left: auto;
  border-collapse: collapse;
  font-size: 12px;
  color: #666;
}

.dm-nk-detail-table td {
  padding: 3px 0;
}

/* Checked count badge */
.dm-checked-count {
  display: inline-block;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 10px;
  background: rgba(36, 196, 95, 0.15);
  color: #1a9e4a;
  margin-left: 8px;
  font-weight: 600;
}

.form-group {
  margin-bottom: 0;
}

.dm-checkbox-align {
  height: calc(2.125rem + 2px);
  display: flex;
  align-items: center;
}

.dm-row-excluded td {
  opacity: 0.4;
  text-decoration: line-through;
}

.dm-manual-price {
  width: 100px;
  text-align: right;
}

.dm-zusatz-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 4px;
  background: rgba(128, 128, 128, 0.05);
  min-height: 44px;
}

/* Collapsible section header */
.dm-collapse-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(128, 128, 128, 0.05);
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6e82a5;
}

.dm-collapse-header:hover {
  background: rgba(128, 128, 128, 0.1);
}

.dm-collapse-hint {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #999;
  margin-left: auto;
  font-size: 12px;
}

/* AI Assistent bar */
.dm-ai-bar {
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.2s;
}

.dm-ai-bar:focus-within {
  border-color: #E8751A;
  box-shadow: 0 0 0 3px rgba(232, 117, 26, 0.08);
}

.dm-ai-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
}

.dm-ai-icon {
  color: #E8751A;
  font-size: 18px;
  flex-shrink: 0;
}

.dm-ai-input {
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  line-height: 1.5;
  background: transparent;
  min-height: 28px;
  overflow: hidden;
}

.dm-ai-input::placeholder {
  color: #aaa;
}

.dm-ai-btn {
  flex-shrink: 0;
  background: #E8751A;
  color: #fff;
  border: none;
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.dm-ai-btn:hover:not(:disabled) {
  background: #d0680f;
  color: #fff;
}

.dm-ai-btn:disabled {
  opacity: 0.4;
  color: #fff;
  background: #E8751A;
}

.dm-ai-error {
  padding: 6px 14px 10px;
  font-size: 12px;
  color: #dc3545;
}

.dm-ai-usage {
  padding: 4px 14px 8px;
  font-size: 11px;
  color: #aaa;
  text-align: right;
}

.dm-price-override {
  color: #E8751A;
  border-bottom: 1px dashed #E8751A;
}
</style>
